import { FormDrawer } from '@components/Drawers/FormDrawer';
import { PrincipalPicker } from '@modules/users/components/PrincipalPicker';
import { SecurityPrincipalShim } from '@modules/users/components/types';
import { Alert, DrawerProps, FormControl, MenuItem, Stack, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { useQueryClient } from '@tanstack/react-query';
import { useQueryInvalidator } from '@utils/useQueryInvalidator';
import { useValidationRules } from '@utils/useValidationRules';
import dayjs, { Dayjs } from 'dayjs';
import { PrincipalType, ProjectTaskStatus, ProjectTaskType, useAddTaskMutation, useGetProjectBudgetQuery, useMyTasksQuery, useParentTaskQuery, useProjectTaskQuery, useProjectTaskSubtasksQuery, useProjectTasksQuery, useUpdateTaskMutation } from 'gql/index';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { getTaskStatusOrderingKey, useTaskMessages } from '../taskUtils';
import { TaskPredecessorsPicker } from './TaskPredecessorsPicker';
import { TaskSelectInput } from './TaskSelectInput/TaskSelectInput';
import { TaskStatusDisplay } from './TaskStatusDisplay';
import { ProjectAutocomplete } from './ProjectAutocomplete';
import { ProjectPickerProjectsQuery } from 'gql/index';
import { allTasksPageQueryKeys } from '../all-tasks-page/hooks/useTasksFilters';

interface Props extends DrawerProps {
  taskId?: number;
  parentTaskId?: number;
  projectId?: number;
  enableProjectSelection?: boolean;
  projectFilter?: (project: ProjectPickerProjectsQuery['projects'][number]) => boolean ;
}

interface FormValues {
  name: string;
  description: string;
  startDate: Dayjs | null
  dueDate: Dayjs | null;
  assignees: SecurityPrincipalShim[];
  status: ProjectTaskStatus;
  predecessorIds: number[];
  parentId: number | null;
}

const defaultValues: FormValues = {
  name: '',
  description: '',
  startDate: null,
  dueDate: null,
  assignees: [],
  status: ProjectTaskStatus.Ready,
  predecessorIds: [],
  parentId: null
};

const isParent = (newParentPath: string | null | undefined, childPath: string | null | undefined) => {
  if (!newParentPath || !childPath) return false;
  return newParentPath.startsWith(childPath);
};

export const TaskFormDrawer: React.FC<Props> = ({ taskId, parentTaskId, projectId, enableProjectSelection, projectFilter, ...drawerProps }) => {
  const defaultInputRef = useRef<HTMLDivElement>(null);
  const { notEmpty } = useValidationRules();
  const { formatMessage } = useIntl();
  const invalidateQuery = useQueryInvalidator();
  const { getTaskName } = useTaskMessages();
  const queryClient = useQueryClient();
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(projectId ?? null);
  const effectiveProjectId = selectedProjectId ?? projectId ?? null;

  const { control, handleSubmit, reset, formState: { isDirty } } = useForm<FormValues>({ defaultValues });
  const startDate = useWatch({ control, name: 'startDate' });

  const { data: task, isFetching } = useProjectTaskQuery({ taskId: taskId ?? 0, projectId: effectiveProjectId ?? 0 }, {
    select: d => d.projectTask,
    enabled: !!taskId && !!effectiveProjectId && drawerProps.open,
    gcTime: 300000,
    staleTime: 300000,
  });

  const { data: parentTask, isFetching: isFetchingParentTask } = useProjectTaskQuery({ taskId: parentTaskId ?? 0, projectId: effectiveProjectId ?? 0 }, { select: d => d.projectTask, enabled: !!parentTaskId && !!effectiveProjectId && drawerProps.open });

  const { data: tasks } = useProjectTasksQuery({ projectId: effectiveProjectId ?? 0 }, {
    select: d => d.projectTasks,
    enabled: !!taskId && !!effectiveProjectId && drawerProps.open,
    gcTime: 300000,
    staleTime: 300000,
  });


  const { data: currentParentTask, isFetching: isFetchingCurrentParentTask } = useParentTaskQuery({ taskId: taskId ?? 0, projectId: effectiveProjectId ?? 0 }, {
    select: d => d.parentTask,
    meta: { nullable: true },
    enabled: !!taskId && !!effectiveProjectId && drawerProps.open
  });

  useEffect(() => {
    if (projectId != null) {
      setSelectedProjectId(projectId);
    }
  }, [projectId]);

  useEffect(() => {

    if (task) {
      reset({
        name: getTaskName(task),
        description: task.description ?? '',
        startDate: task.startDate ? dayjs(task.startDate) : null,
        dueDate: task.dueDate ? dayjs(task.dueDate) : null,
        assignees: task.assignees.map<SecurityPrincipalShim>(a => ({ principalType: PrincipalType.User, id: a.id }))
          .concat(task.assigneeGroups.map<SecurityPrincipalShim>(a => ({ principalType: PrincipalType.SecurityGroup, id: a.id }))),
        status: task.status,
        predecessorIds: task.predecessors.map(p => p.id),
        parentId: currentParentTask?.id || null
      });
    } else {
      reset({
        ...defaultValues,
        startDate: (parentTask && parentTask.startDate) ? dayjs(parentTask.startDate) : defaultValues.startDate,
        dueDate: (parentTask && parentTask.startDate) ? dayjs(parentTask.startDate).add(7, 'days') : defaultValues.dueDate,
        parentId: parentTaskId ?? null
      });
    }
    setTimeout(() => defaultInputRef.current?.focus(), 500);
  }, [reset, drawerProps.open, task, getTaskName, parentTaskId, parentTask, currentParentTask?.id, effectiveProjectId]);

  const { mutate: addTask, isPending: isAddLoading } = useAddTaskMutation({
    onSuccess: () => {
      drawerProps.onClose?.({}, 'backdropClick');
      if (effectiveProjectId) {
        invalidateQuery(useMyTasksQuery, { projectId: effectiveProjectId });
        invalidateQuery(useProjectTasksQuery, { projectId: effectiveProjectId });
        invalidateQuery(useGetProjectBudgetQuery, { id: effectiveProjectId });
      }
      if (parentTaskId != null) {
        invalidateQuery(useProjectTaskSubtasksQuery, { taskId: parentTaskId });
      }
      queryClient.invalidateQueries({ queryKey: allTasksPageQueryKeys.root } );
    }
  });

  const { mutate: updateTask, isPending: isUpdatePending } = useUpdateTaskMutation({
    onSuccess: () => {
      drawerProps.onClose?.({}, 'backdropClick');
      if (effectiveProjectId) {
        invalidateQuery(useMyTasksQuery, { projectId: effectiveProjectId });
        invalidateQuery(useProjectTasksQuery, { projectId: effectiveProjectId });
        invalidateQuery(useGetProjectBudgetQuery, { id: effectiveProjectId });
      }
      if (task?.id != null) {
        invalidateQuery(useProjectTaskQuery, { taskId: task.id, projectId: effectiveProjectId! });
      }
      queryClient.invalidateQueries({ queryKey: allTasksPageQueryKeys.root } );
    }
  });

  const isLoading = isAddLoading || isUpdatePending || isFetching || isFetchingParentTask;
  const isProjectSelectionRequired = !!(enableProjectSelection && !effectiveProjectId && !taskId);

  const onSubmit = (values: FormValues) => {
    if (!effectiveProjectId) {
      return;
    }

    if (task) {
      updateTask({
        input: {
          projectId: effectiveProjectId,
          taskId: task.id,
          name: values.name,
          description: values.description,
          assignees: values.assignees.map(a => ({ id: a.id, principalType: a.principalType })),
          startDate: dayjs(values.startDate).utc(),
          dueDate: dayjs(values.dueDate).utc(),
          status: values.status,
          predecessorIds: values.predecessorIds,
          parentTaskId: values.parentId
        }
      });
    } else {
      addTask({
        input: {
          projectId: effectiveProjectId,
          name: values.name,
          description: values.description,
          assignees: values.assignees.map(a => ({ id: a.id, principalType: a.principalType })),
          startDate: values.startDate?.utc(),
          dueDate: values.dueDate?.utc(),
          status: values.status,
          predecessorIds: values.predecessorIds,
          parentTaskId: values.parentId
        }
      });
    }
  };

  const isEditingSystemTask = task != null && task.type !== ProjectTaskType.Custom;

  const getHeader = () => {
    if (task) {
      return formatMessage({ id: 'Update task' });
    }

    if (parentTaskId) {
      return formatMessage({ id: 'Add subtask' });
    }

    return formatMessage({ id: 'Add new task' });
  };

  return (
    <FormDrawer
      {...drawerProps}
      isFormDirty={isDirty}
      showFooter
      isSubmitting={isLoading}
      onSave={handleSubmit(onSubmit)}
      header={getHeader()}
    >
      <Stack gap={2} p={2}>
        {enableProjectSelection && (
          <ProjectAutocomplete
            value={effectiveProjectId}
            onChange={setSelectedProjectId}
            filter={projectFilter}
          />
        )}
        {task != null && task.type !== ProjectTaskType.Custom &&
          <Alert severity='info'>{formatMessage({ id: 'This is a system task. Only the scheduling dates can be updated.' })}</Alert>
        }
        <Controller
          control={control}
          name='name'
          rules={{ validate: notEmpty }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              required
              inputRef={defaultInputRef}
              {...field}
              disabled={isLoading || isEditingSystemTask || isProjectSelectionRequired}
              label={formatMessage({ id: 'Name' })}
              error={!!error}
              helperText={error?.message}
              autoComplete='off'
            />
          )}
        />

        <Controller
          control={control}
          name='description'
          render={({ field }) => (
            <TextField
              {...field}
              rows={3}
              multiline
              disabled={isLoading || isEditingSystemTask || isProjectSelectionRequired}
              label={formatMessage({ id: 'Description' })}
            />
          )}
        />

        <Stack direction='row' spacing={1} >
          <Controller
            control={control}
            name='startDate'
            render={({ field, fieldState: { error } }) => (
              <DatePicker
                {...field}
                slotProps={{
                  textField: {
                    disabled: isLoading || isProjectSelectionRequired,
                    fullWidth: true,
                    error: !!error,
                    helperText: error?.message,
                  },
                }}
                label={formatMessage({ id: 'Start date' })}
              />
            )}
          />

          <Controller
            control={control}
            name='dueDate'
            rules={{
              validate: {
                afterStartDate: v => v == null || (startDate != null && !v.isBefore(startDate.startOf('day'))) || formatMessage({ id: 'Due date must be after start date' }),
              }
            }}
            render={({ field, fieldState: { error } }) => (
              <>
                <DatePicker
                  {...field}
                  slotProps={{
                    textField: {
                      disabled: isLoading || isProjectSelectionRequired,
                      fullWidth: true,
                      error: !!error,
                      helperText: error?.message,
                    },
                  }}
                  label={formatMessage({ id: 'Due date' })}
                />
              </>
            )}
          />
        </Stack>

        <Controller
          control={control}
          name='assignees'
          render={({ field, fieldState: { error } }) => (
            <FormControl>
              {effectiveProjectId ? (
                <PrincipalPicker
                  projectId={effectiveProjectId}
                  multiple
                  includeMe
                  value={field.value}
                  onChange={field.onChange}
                  label={formatMessage({ id: 'Assigned to' })}
                  error={error?.message}
                />
              ) : (
                <TextField
                  disabled
                  label={formatMessage({ id: 'Assigned to' })}
                  helperText={formatMessage({ id: 'Select a project first' })}
                />
              )}
            </FormControl>
          )}
        />

        <Controller
          control={control}
          name='status'
          render={({ field }) => (
            <TextField
              {...field}
              disabled={isLoading || isEditingSystemTask}
              label={formatMessage({ id: 'Status' })}
              select
              slotProps={{
                select: {
                  renderValue: status => <TaskStatusDisplay status={status as ProjectTaskStatus} />
                }
              }}
            >
              {_.orderBy(Object.values(ProjectTaskStatus), p => getTaskStatusOrderingKey(p)).map(status => (
                <MenuItem key={status} value={status}>
                  <TaskStatusDisplay status={status} />
                </MenuItem>
              ))}
            </TextField>
          )}
        />

        <Controller
          control={control}
          name='predecessorIds'
          render={({ field }) => (
            effectiveProjectId ? (
              <TaskPredecessorsPicker
                taskId={taskId}
                projectId={effectiveProjectId}
                {...field}
                disabled={isLoading || isEditingSystemTask || isProjectSelectionRequired}
              />
            ) : (
              <TextField
                disabled
                label={formatMessage({ id: 'Predecessors' })}
                helperText={formatMessage({ id: 'Select a project first' })}
              />
            )
          )}
        />

        <Controller
          control={control}
          name='parentId'
          rules={{
            validate: {
              isNotChild: parentId => {
                const parent = tasks?.find(t => t.id === parentId);
                if (isParent(parent?.hierarchyPath, task?.hierarchyPath)) {
                  return formatMessage({ id: 'Parent task cannot be a child of the task' });
                }
              }
            }
          }}
          render={({ field, fieldState: { error } }) => (
            effectiveProjectId ? (
              <TaskSelectInput
                value={field.value}
                projectId={effectiveProjectId}
                onChange={field.onChange}
                error={!!error}
                helperText={error?.message}
                disabled={!!parentTaskId || isFetchingCurrentParentTask || isLoading || isEditingSystemTask || isProjectSelectionRequired}
              />
            ) : (
              <TextField
                disabled
                label={formatMessage({ id: 'Parent task' })}
                helperText={formatMessage({ id: 'Select a project first' })}
              />
            )
          )}
        />
      </Stack>
    </FormDrawer>
  );
};